<template>
  <SidebarCaption v-model="isOpened" v-bind="{ caption }" />
  <draggable
    v-if="modelValue"
    :model-value="draggableModel"
    v-bind="options"
    class="ui-pivot-table-sidebar-block"
    :class="mainClasses"
    @add="handleAdd"
    @update="handleUpdate"
  >
    <template #item="{ element: [field, item] }">
      <SidebarBlockField v-bind="{ field, item }" @toggle="handleToggle" />
    </template>
  </draggable>
  <div v-else class="ui-pivot-table-sidebar-block">
    <UISkeleton class="ui-pivot-table-sidebar-block__skeleton" />
    <UISkeleton class="ui-pivot-table-sidebar-block__skeleton" />
  </div>
</template>

<script setup lang="ts">
import { computed, ref } from 'vue'
import draggable from 'vuedraggable'
import { cloneDeep, orderBy } from 'lodash'

import { PivotTableConfig } from '../utils/types'
import { PivotTableGroup } from '../utils/enums'

import { DRAG_OPTIONS } from './utils/const'

import SidebarBlockField from './SidebarBlockField.vue'
import SidebarCaption from './SidebarCaption.vue'
import { UISkeleton } from '@ui/skeletons'

type Props = {
  caption: string
  group?: string
  section: PivotTableGroup
}

const { group, section } = defineProps<Props>()

const modelValue = defineModel<PivotTableConfig>()

const isOpened = ref(false)

const draggableModel = computed(() => {
  if (!modelValue.value) return []
  return orderBy(
    Object.entries(modelValue.value.fields || {}).filter(
      ([, field]) => field.group === section,
    ),
    item => item[1].index,
  )
})

const mainClasses = computed(() => ({
  'ui-pivot-table-sidebar-block--opened': isOpened.value,
}))

const options = computed(() => ({
  ...DRAG_OPTIONS,
  group,
}))

const handleAdd = (event: any) => {
  if (!modelValue.value?.fields) return
  const fields = cloneDeep(modelValue.value?.fields)
  const key = event.item._underlying_vm_[0]
  fields[key].group = section
  fields[key].index = event.newIndex
  draggableModel.value.forEach(([key], index) => {
    fields[key].index = index + Number(index >= event.newIndex)
  })
  modelValue.value = {
    ...modelValue.value,
    fields,
  }
}

const handleUpdate = (event: any) => {
  if (event.pullMode || !modelValue.value?.fields) return
  const fields = cloneDeep(modelValue.value?.fields)
  const key = event.item._underlying_vm_[0]
  const oldIndex = event.oldIndex
  const newIndex = event.newIndex
  fields[key].index = newIndex
  const reverse = newIndex < oldIndex
  draggableModel.value.forEach(([fieldKey], index) => {
    if (key === fieldKey) return
    let diff = 0
    if (reverse && index < oldIndex) {
      diff = Number(index >= event.newIndex)
    } else if (index > oldIndex) {
      diff = index > event.newIndex ? +1 : -1
    }
    fields[fieldKey].index = index + diff
  })
  modelValue.value = {
    ...modelValue.value,
    fields,
  }
}

const handleToggle = (key: string, enabled: boolean) => {
  if (!modelValue.value?.fields) return
  const fields = cloneDeep(modelValue.value?.fields)
  fields[key].enabled = enabled
  modelValue.value = {
    ...modelValue.value,
    fields,
  }
}
</script>

<script lang="ts">
export default {
  name: 'SidebarBlock',
}
</script>

<style>
.ui-pivot-table-sidebar-block {
  @apply hidden sm:block;
  @apply py-4 px-2 space-y-1;

  &--opened {
    @apply block;
  }

  &__skeleton {
    @apply h-[1.5rem];
    @apply rounded-full;
  }
}
</style>
